import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { ExcelFile, ExcelSheet, ExcelColumn } from 'react-xlsx-wrapper';
import CommonHeader from '../../util_components/common_header';
import CustomModal from '../../util_components/custom_modal';
import CustomSingleSelect from '../../util_components/custom_single_select';
import CustomDatePicker from '../../util_components/custom_date_picker';

import { image_error_replace, get_date_filter_data_timezone_utc } from '../../../util_functions';
import {
  day_format,
  time_format,
  date_drop_for_report,
  trial_end_date_drop_for_report,
  conciergeV3DownloadList,
  EthnicityOptions
} from '../../../constants';
import { get_api, post_api, put_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { get_api_url } from '../../../utils/urls';
import SmsWindow from './sms_window';
import './concierge_v3.css';

import 'react-datepicker/dist/react-datepicker.css';

import UserRole from '../../../myt_chat/chat/enums/UserRole';
import { openChatWithUserUUID } from '../../chat/chat';
import { EditIcon } from '../../../utils/svg';
import { teacherFullNameOf } from '../../../utils/teacher_name';

class ConciergeV3 extends Component {
  state = {
    loading: true,
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    start_date_for_trial: moment().format('YYYY-MM-DD'),
    end_date_for_trial: moment().format('YYYY-MM-DD'),
    students: [],
    history: '',
    history_open: '',
    open_info: false,
    student_info: {},
    concierge_teachers: [],
    concierge_teacher_uuid: 'all',
    selected_iana_timezone: 'ALL',
    is_header_sticky: false,
    sms_window_open: false,
    sms_student: '',
    sms_student_name: '',
    show_concierge_detail: false,
    s_uuid: '',
    concierge_detail: '',
    member_filter: 'ALL',
    sort_by: 'DEFAULT',
    onboarding_score_for_popup: '',
    iana_abbr_list: [],
    original_message_uuid: '',
    pods: [],
    selected_pod: 'ALL',
    tz_region_map: {},
    date_filter_type: 'TODAY',
    trial_end_date_filter: '',
    open_ethnicity_popup: false,
    concierge_entered_ethnicity: '',
    message_dispatch_method: '',
    is_allowed_whatsapp: false,
    is_allowed_sms: false,
    first_1on1_filter: 'ALL',
    selected_day: 'ALL'
  };

  headerRef = React.createRef();

  async componentDidMount() {
    // Widen the shared shell container for THIS page only. common.css caps every
    // .common-page-container at width:calc(90% - 60px) + 5% side margins, which
    // starves the content. Reverted on unmount, so no other page is affected.
    const cpc = document.querySelector('.common-page-container');
    if (cpc) cpc.classList.add('cv3-fullwidth');

    this.load_data();
    this.load_pods();

    if (this.props.teachers_list_status === 'success') {
      this.set_teachers_list();
    }

    if (this.props.teachers_list_status === 'none') {
      this.props.get_teachers_list();
    }
  }

  componentWillUnmount() {
    const cpc = document.querySelector('.common-page-container');
    if (cpc) cpc.classList.remove('cv3-fullwidth');
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.teachers_list_status === 'loading' &&
      this.props.teachers_list_status === 'success'
    ) {
      this.set_teachers_list();
    }
    if (prevProps.location.pathname.toString() !== this.props.location.pathname.toString()) {
      this.setState({ date_filter_type: 'TODAY' }, () => {
        this.clear_concierge();
        this.clear_timezone();
        this.clear_pod();
        this.clear_first_1on1();
        this.clear_day();
        this.load_data();
      });
    }
  }

  load_pods = () => {
    // Pods are the PROD-6787 regions — used here only to label the pod dropdown (id → name).
    // Students are bucketed into pods by timezone via resolve_pod_timezones (the exact
    // assignment-engine logic), NOT by the regions' country lists. Best-effort: on error we
    // leave pods empty so the rest of the report keeps working.
    get_api(get_api_url('/v2/cms_user/concierge_pods/regions'), true)
      .then((res) => {
        this.setState({ pods: (res.data && res.data.data) || [] });
      })
      .catch((e) => {
        let err_message;
        try {
          err_message = e.response.data.message;
        } catch (err) {
          err_message = 'Failed to load pods.';
        }
        this.props.set_notification_variable(true, 'error', err_message);
      });
  };

  set_teachers_list = () => {
    const all_teachers = this.props.teachers_list.filter((t) => t.opt_in_concierge === 1);
    const teachers_list = all_teachers.map((teacher) => ({
      value: teacher.uuid,
      label: teacherFullNameOf(teacher)
    }));
    teachers_list.sort((a, b) => a.label.localeCompare(b.label));
    this.setState({ concierge_teachers: [] }, () => {
      this.setState({
        concierge_teachers: [{ value: 'all', label: 'ALL' }, ...teachers_list]
      });
    });
  };

  load_data = () =>
    // Clear tz_region_map with students; resolve_pod_timezones repopulates it once the new cohort
    // loads, so a selected pod never filters new students against the previous cohort's map.
    this.setState({ loading: true, students: [], tz_region_map: {} }, () => {
      const {
        date_filter_type,
        start_date,
        end_date,
        trial_end_date_filter,
        end_date_for_trial,
        start_date_for_trial
      } = this.state;
      const url = get_api_url('get_concierge_students');
      let date_data;
      if (trial_end_date_filter !== '') {
        date_data = get_date_filter_data_timezone_utc(
          trial_end_date_filter,
          start_date_for_trial,
          end_date_for_trial
        );
      } else if (date_filter_type !== '') {
        date_data = get_date_filter_data_timezone_utc(date_filter_type, start_date, end_date);
      }

      const payload = {
        start_date: date_data.utc_start_date,
        end_date: date_data.utc_end_date,
        iana_timezone: this.props.iana_timezone,
        is_back_pain_client: 0,
        get_by_trial_end_date: trial_end_date_filter !== '' ? 1 : 0
      };
      post_api(url, payload, true)
        .then((res) => {
          const student_data = res.data.student_data || [];
          this.setState(
            {
              students: [...student_data],
              iana_abbr_list: [...res.data.iana_abbr_list]
            },
            () => {
              this.setState({ loading: false });
              this.resolve_pod_timezones(student_data);
            }
          );
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.message;
          } catch (err) {
            err_message = 'Some error occured. Please contact dev team.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ loading: false });
        });
    });

  handle_date_chage = (date, component) => {
    this.setState({ [component]: date });
  };

  change_date_filter = (e) =>
    this.setState(
      { date_filter_type: e.target.value, students: [], trial_end_date_filter: '' },
      () => {
        this.load_data();
      }
    );

  change_trial_end_date_filter = (e) => {
    this.setState(
      { trial_end_date_filter: e.target.value, students: [], date_filter_type: '' },
      () => {
        this.load_data();
      }
    );
  };

  handle_date_change = (name, val) => this.setState({ [name]: val });

  change_concierge = (e) => {
    this.setState({ concierge_teacher_uuid: e.target.value });
  };

  change_iana_timezone = (e) => {
    this.setState({ selected_iana_timezone: e.target.value });
  };

  change_pod = (e) => {
    this.setState({ selected_pod: e.target.value });
  };

  change_first_1on1 = (e) => this.setState({ first_1on1_filter: e.target.value });

  clear_first_1on1 = () => this.setState({ first_1on1_filter: 'ALL' });

  change_member_filter = (e) => this.setState({ member_filter: e.target.value });

  clear_member_filter = () => this.setState({ member_filter: 'ALL' });

  change_sort = (e) => this.setState({ sort_by: e.target.value });

  change_day = (e) => this.setState({ selected_day: e.target.value });

  clear_day = () => this.setState({ selected_day: 'ALL' });

  get_day_options = () => {
    const opts = [{ value: 'ALL', label: 'ALL' }];
    for (let d = 1; d <= 30; d += 1) opts.push({ value: String(d), label: `Day ${d}` });
    return opts;
  };

  // Day 1 = the creation day, counted in the student's own timezone (PROD-6787).
  // account_created_date is a UTC datetime from the DB — parse it as UTC (moment.utc)
  // before shifting to the student's tz, so the day bucket can't drift by one.
  get_student_day = (student) => {
    const created = student.account_created_date;
    if (!created) return '';
    const tz = student.iana_timezone || this.props.iana_timezone;
    return (
      moment().tz(tz).startOf('day').diff(moment.utc(created).tz(tz).startOf('day'), 'days') + 1
    );
  };

  // Human label for the first-1on1 session status column in the download.
  // first_1on1_session_status now reflects the student's first ATTENDED 1on1:
  // the backend returns 'FINISHED' when they have finished at least one 1on1, else
  // null. So the download label is finished-or-not; null means "no finished session
  // yet" (covers not-booked, upcoming, and no-showed), hence "Not finished".
  get_first_1on1_status_label = (status) => (status === 'FINISHED' ? 'Finished' : 'Not finished');

  load_concierge_data = () => {
    this.setState({ loading: true }, () => {
      const { s_uuid } = this.state;
      const url = get_api_url('get_concierge_onboarding_score_details');
      const payload = {
        student_uuid: s_uuid
      };
      post_api(url, payload, true)
        .then((res) => {
          this.setState({ loading: false, concierge_detail: res.data });
        })
        .catch((e) => {
          let err_message;
          try {
            err_message = e.response.data.message;
          } catch (err) {
            err_message = 'Some error occured. Please contact dev team.';
          }
          this.props.set_notification_variable(true, 'error', err_message);
          this.setState({ loading: false });
        });
    });
  };

  open_concierge_detail = (student_uuid, onboarding_score) => {
    this.setState({
      show_concierge_detail: true,
      s_uuid: student_uuid,
      onboarding_score_for_popup: onboarding_score
    });
    this.load_concierge_data();
  };

  concierge_profile = () => {
    const { concierge_detail, onboarding_score_for_popup } = this.state;
    return (
      <div>
        <table className="table-auto">
          <thead>
            <th>Onboarding Score</th>
            <th>{onboarding_score_for_popup}</th>
          </thead>
          <tbody style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
            <tr>
              <th className="text-justify">Ethnicity Score (Western 1 Indian 2):</th>
              <td>{concierge_detail.ethnicity_score}</td>
            </tr>
            <tr>
              <th className="text-justify">
                Yoga level score (Newbie / Beginner 1 Intermediate / Advanced 2):
              </th>
              <td>{concierge_detail.yoga_level_score}</td>
            </tr>
            <tr>
              <th className="text-justify">Learner type score (Group 1 One-on-One 1.5):</th>
              <td>{concierge_detail.learner_type_score}</td>
            </tr>
            <tr>
              <th className="text-justify">Funnel score (Referral 4 Organic 3 Paid 1):</th>
              <td>{concierge_detail.funnel_score}</td>
            </tr>
            <tr>
              <th className="text-justify">Gender score (Male 1.25 Female 1):</th>
              <td>{concierge_detail.gender_score}</td>
            </tr>
            <tr>
              <th className="text-justify">
                Signup time score (7 am - 7 pm in user timezone 1 Else 0.5):
              </th>
              <td>{concierge_detail.signup_time_score}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  close_concierge_detail = () => {
    this.setState({ show_concierge_detail: false });
  };

  clear_trial_end_date = () => {
    this.setState({ trial_end_date_filter: '' });
  };

  clear_onboarding_date = () => {
    this.setState({ date_filter_type: '' });
  };

  render_filters = () => {
    const {
      date_filter_type,
      start_date,
      end_date,
      concierge_teachers,
      concierge_teacher_uuid,
      selected_iana_timezone,
      iana_abbr_list,
      selected_pod,
      trial_end_date_filter,
      start_date_for_trial,
      end_date_for_trial,
      first_1on1_filter,
      selected_day,
      member_filter
    } = this.state;
    const ctrl = 'w-full md:w-[210px]';
    return (
      <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[var(--border-default)] bg-[var(--surface-card)] p-3 md:flex-row md:flex-wrap md:items-end">
        <div className={ctrl}>
          <CustomSingleSelect
            label="Trial start date"
            options={date_drop_for_report}
            onChange={this.change_date_filter}
            value={date_filter_type}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_onboarding_date}
            defaultOptionDisabled={trial_end_date_filter !== ''}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Trial end date"
            options={trial_end_date_drop_for_report}
            onChange={this.change_trial_end_date_filter}
            value={trial_end_date_filter}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_trial_end_date}
            defaultOptionDisabled={date_filter_type !== ''}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Concierge"
            options={concierge_teachers}
            onChange={this.change_concierge}
            value={concierge_teacher_uuid}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_concierge}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Timezone"
            options={iana_abbr_list}
            onChange={this.change_iana_timezone}
            value={selected_iana_timezone}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_timezone}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Pod"
            options={this.get_pod_options()}
            onChange={this.change_pod}
            value={selected_pod}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_pod}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Member"
            options={[
              { value: 'ALL', label: 'All' },
              { value: 'MEMBERS', label: 'Members' },
              { value: 'NON_MEMBERS', label: 'Non members' }
            ]}
            onChange={this.change_member_filter}
            value={member_filter}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_member_filter}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="1st 1on1 Finished"
            options={[
              { value: 'ALL', label: 'ALL' },
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ]}
            onChange={this.change_first_1on1}
            value={first_1on1_filter}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_first_1on1}
          />
        </div>
        <div className={ctrl}>
          <CustomSingleSelect
            label="Day"
            options={this.get_day_options()}
            onChange={this.change_day}
            value={selected_day}
            style={{ width: '100%' }}
            showClear
            clear_fn={this.clear_day}
          />
        </div>
        {date_filter_type === 'CUSTOM' ? (
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end md:w-auto">
            <div className="daily-report-item">
              <CustomDatePicker
                label="Start Date"
                value={start_date}
                handleChange={(val) => this.handle_date_change('start_date', val)}
                maxDate={end_date}
              />
            </div>
            <div className="daily-report-item">
              <CustomDatePicker
                label="End Date"
                value={end_date}
                handleChange={(val) => this.handle_date_change('end_date', val)}
                minDate={start_date}
                maxDate={moment().format('YYYY-MM-DD')}
              />
            </div>
            <button className="m-0 h-[46px]" onClick={this.load_data}>
              Submit
            </button>
          </div>
        ) : null}
        {trial_end_date_filter === 'CUSTOM' ? (
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-end md:w-auto">
            <div className="daily-report-item">
              <CustomDatePicker
                label="Start Date"
                value={start_date_for_trial}
                handleChange={(val) => this.handle_date_change('start_date_for_trial', val)}
              />
            </div>
            <div className="daily-report-item">
              <CustomDatePicker
                label="End Date"
                value={end_date_for_trial}
                handleChange={(val) => this.handle_date_change('end_date_for_trial', val)}
              />
            </div>
            <button className="m-0 h-[46px]" onClick={this.load_data}>
              Submit
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  clear_concierge = () => this.setState({ concierge_teacher_uuid: 'all' });

  clear_timezone = () => this.setState({ selected_iana_timezone: 'ALL' });

  clear_pod = () => this.setState({ selected_pod: 'ALL' });

  // resolve_pod_timezones maps each student's IANA timezone to a concierge pod using the SAME
  // resolver the assignment engine uses (goserver POST /concierge_pods/resolve_timezones):
  // Americas split East/West by tz_group, rest of world by tz→country→pod. Best-effort: on error
  // we leave the map empty and apply_pod_filter falls back to showing everyone.
  resolve_pod_timezones = (students_list) => {
    const timezones = [
      ...new Set((students_list || []).map((s) => s.iana_timezone).filter(Boolean))
    ];
    if (timezones.length === 0) {
      this.setState({ tz_region_map: {} });
      return;
    }
    post_api(get_api_url('/v2/cms_user/concierge_pods/resolve_timezones'), { timezones }, true)
      .then((res) => {
        const list = (res.data && res.data.data) || [];
        const tz_region_map = {};
        list.forEach((r) => {
          tz_region_map[r.timezone] = {
            region_id: r.region_id,
            region_name: r.region_name,
            resolved: r.resolved
          };
        });
        this.setState({ tz_region_map });
      })
      .catch(() => {
        this.setState({ tz_region_map: {} });
      });
  };

  get_pod_options = () => {
    const { pods } = this.state;
    return [
      { value: 'ALL', label: 'ALL Pods' },
      ...pods.map((pod) => ({ value: String(pod.id), label: pod.name })),
      { value: 'UNMAPPED', label: '(Unmapped)' }
    ];
  };

  apply_pod_filter = (students_list) => {
    const { selected_pod, tz_region_map } = this.state;
    if (!selected_pod || selected_pod === 'ALL') {
      return students_list;
    }
    // "(Unmapped)" bucket: no timezone, or a timezone that resolves to no pod (e.g. India, or a
    // country without a pod). Shown separately per PROD-6787 (1.a).
    if (selected_pod === 'UNMAPPED') {
      return students_list.filter((s) => {
        const entry = tz_region_map[s.iana_timezone];
        return !s.iana_timezone || !entry || !entry.resolved;
      });
    }
    // A specific pod: keep students whose timezone resolves to that pod's region id.
    const pod_id = Number(selected_pod);
    return students_list.filter((s) => {
      const entry = tz_region_map[s.iana_timezone];
      return !!entry && entry.resolved && entry.region_id === pod_id;
    });
  };

  open_history = (e, history) => {
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    this.setState({ history, history_open: true });
  };

  open_chat = async (student) => {
    openChatWithUserUUID(student.uuid, UserRole.STUDENT);
  };

  get_href = (uuid, type) => {
    let final_href = null;
    if (type === 'YOGA') {
      final_href = `/sessiondetails?id=${uuid}`;
    } else {
      final_href = `/groupsessiondetails?session_uuid=${uuid}`;
    }
    return final_href;
  };

  // Declarative sort applied to a copy of the already-filtered list at render time
  // (see render_data). Driven by the `sort_by` toolbar dropdown. Kept out of state
  // mutation on purpose: the previous imperative sort mutated `students` and reset
  // its direction flags on every load_data, so the control effectively never stuck.
  // 'DEFAULT' preserves the server order (trial date DESC).
  sort_students = (list) => {
    const { sort_by } = this.state;
    const num = (v) => parseFloat(v) || 0;
    const sorters = {
      ONB_DESC: (a, b) => num(b.onboarding_score) - num(a.onboarding_score),
      ONB_ASC: (a, b) => num(a.onboarding_score) - num(b.onboarding_score),
      ACT_DESC: (a, b) => num(b.activity_score) - num(a.activity_score),
      ACT_ASC: (a, b) => num(a.activity_score) - num(b.activity_score)
    };
    const sorter = sorters[sort_by];
    return sorter ? [...list].sort(sorter) : list;
  };

  activate_on_key = (e, fn) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  render_mobile_student_card = (student) => {
    const student_url = `/studentprofile?id=${student.uuid}`;
    const concierge_url = `/teacherprofile?id=${student.concierge_uuid}`;
    const ethniCity =
      student.concierge_entered_ethnicity !== ''
        ? student.concierge_entered_ethnicity
        : student.ml_ethnicity;
    const tz = this.props.iana_timezone;
    const can_message =
      !!student.phone_no && student.phone_no !== '' && student.message_dispatch_method !== 'none';
    const is_wa = student.message_dispatch_method === 'whatsapp';
    const funnel = [student.funnel_url, student.funnel_type, student.offer_type]
      .filter(Boolean)
      .join(' · ');
    const start_full = `${moment(student.created_date).tz(tz).format(day_format)}, ${moment(student.created_date).tz(tz).format(time_format)}`;
    const end_full = `${moment(student.trial_end_date).tz(tz).format(day_format)}, ${moment(student.trial_end_date).tz(tz).format(time_format)}`;
    const trialFull = `${start_full} → ${end_full}`;
    const start_short = moment(student.created_date).tz(tz).format(day_format);
    const end_short = moment(student.trial_end_date).tz(tz).format(day_format);
    const one_on_one = `${student.yoga_sessions_finshed}/${student.yoga_sessions_scheduled}`;
    const group = `${student.group_sessions_finshed}/${student.group_sessions_scheduled}`;

    const member_badge = (
      <span className="cv3-member-cell">
        <span className={`cv3-badge ${student.is_member === 1 ? 'cv3-yes' : 'cv3-no'}`}>
          {student.is_member === 1 ? 'Yes' : 'No'}
        </span>
        {student.is_member === 1 && student.is_family_member === 1 ? (
          <span className="cv3-family-tag">Family</span>
        ) : null}
      </span>
    );
    const replied_badge =
      student.to_student == 1 ? (
        <span className="cv3-badge cv3-yes">Yes</span>
      ) : student.to_student == 0 ? (
        <span className="cv3-badge cv3-no">No</span>
      ) : (
        <span className="cv3-dash">&mdash;</span>
      );
    const concierge_el = student.concierge_uuid ? (
      <a href={concierge_url}>{student.concierge_name}</a>
    ) : (
      student.concierge_name || '--'
    );
    const onb_ico = (
      <span
        className="cv3-info-ico"
        role="button"
        tabIndex={0}
        title="Onboarding detail"
        onClick={() => this.open_concierge_detail(student.uuid, student.onboarding_score)}
        onKeyDown={(e) =>
          this.activate_on_key(e, () =>
            this.open_concierge_detail(student.uuid, student.onboarding_score)
          )
        }
      >
        &#9432;
      </span>
    );
    const edit_ico = (
      <span
        className="cv3-edit"
        role="button"
        tabIndex={0}
        title="Edit ethnicity"
        onClick={() => this.open_ethnicity_popup(student)}
        onKeyDown={(e) => this.activate_on_key(e, () => this.open_ethnicity_popup(student))}
      >
        {EditIcon()}
      </span>
    );
    const history_link = student.health_history ? (
      <span
        className="cv3-hist"
        role="button"
        tabIndex={0}
        onClick={(e) => this.open_history(e, student.health_history)}
        onKeyDown={(e) =>
          this.activate_on_key(e, () => this.open_history(null, student.health_history))
        }
      >
        {' · '}
        History
      </span>
    ) : null;
    const actions = (
      <div className="cv3-acts">
        <button className="cv3-btn cv3-btn-chat" onClick={() => this.open_chat(student)}>
          Chat
        </button>
        {can_message ? (
          <button
            className={`cv3-btn ${is_wa ? 'cv3-btn-wa' : 'cv3-btn-sms'}`}
            onClick={() => this.open_sms_window(student)}
          >
            <span className="cv3-send">Send </span>
            {is_wa ? 'WA' : 'SMS'}
          </button>
        ) : null}
      </div>
    );

    return (
      <div
        className={`cv3-rec ${student.is_member === 1 ? 'cv3-row-member' : ''}`}
        key={student.uuid}
      >
        {/* ---- table row (container >= 820px) ---- */}
        <div className="cv3-trow">
          <div className="cv3-cell-name">
            <img
              src={student.profile_photo}
              onError={image_error_replace}
              alt=""
              className="cv3-av"
            />
            <span className="id">
              <span className="cv3-trunc" style={{ display: 'block' }}>
                <a
                  className="cv3-name"
                  href={student_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {student.name}
                </a>{' '}
                {edit_ico}
              </span>
              {student.client_email ? (
                <span
                  className="cv3-email cv3-trunc"
                  style={{ display: 'block' }}
                  title={student.client_email}
                >
                  {student.client_email}
                </span>
              ) : null}
              <span className="cv3-loc cv3-trunc" style={{ display: 'block' }}>
                {student.iana_timezone}
                {ethniCity ? ` · ${ethniCity}` : ''}
                {history_link}
              </span>
              <span className="cv3-conc-line cv3-trunc" style={{ display: 'block' }}>
                {concierge_el}
              </span>
            </span>
          </div>
          <div
            className="cv3-stack cv3-mono"
            style={{ fontSize: '11.5px', lineHeight: 1.5 }}
            title={trialFull}
          >
            <div className="a">{start_full}</div>
            <div className="b">{end_full}</div>
          </div>
          <div className="cv3-center">{member_badge}</div>
          <div
            className="cv3-sess cv3-center cv3-mono"
            style={{ fontSize: '12px', lineHeight: 1.6 }}
          >
            <div>1on1: {one_on_one}</div>
            <div>GC: {group}</div>
          </div>
          <div className="cv3-center">{replied_badge}</div>
          <div className="cv3-cell-det cv3-det">
            <div className="l1">
              <b>Onb</b> {student.onboarding_score} {onb_ico} &nbsp; <b>Act</b>{' '}
              {student.activity_score} &nbsp; <b>Cr</b> {student.credits}
            </div>
            <div className="cv3-trunc" title={funnel}>
              {funnel || '--'}
            </div>
          </div>
          <div className="cv3-cell-acts">{actions}</div>
        </div>

        {/* ---- boxed card (container < 820px) ---- */}
        <div className="cv3-card">
          <div className="cv3-card-hd">
            <img
              src={student.profile_photo}
              onError={image_error_replace}
              alt=""
              className="cv3-av"
            />
            <span className="id">
              <span style={{ display: 'block' }}>
                <a
                  className="cv3-name"
                  href={student_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {student.name}
                </a>{' '}
                {edit_ico}
              </span>
              {student.client_email ? (
                <span
                  className="cv3-email cv3-trunc"
                  style={{ display: 'block' }}
                  title={student.client_email}
                >
                  {student.client_email}
                </span>
              ) : null}
              <span className="cv3-loc cv3-trunc" style={{ display: 'block' }}>
                {student.iana_timezone}
                {history_link}
              </span>
              <span className="cv3-conc-line cv3-trunc" style={{ display: 'block' }}>
                {concierge_el}
              </span>
            </span>
            {ethniCity ? <span className="cv3-eth">{ethniCity}</span> : null}
          </div>
          <div className="cv3-boxes">
            <div className="cv3-box" title={trialFull}>
              <div className="bl">Trial</div>
              <div
                className="cv3-mono"
                style={{ fontSize: '12px', fontWeight: 600, overflowWrap: 'anywhere' }}
              >
                {start_short} &rarr; {end_short}
              </div>
            </div>
            <div className="cv3-box">
              <div className="bl">Member</div>
              <div className="bv">{member_badge}</div>
            </div>
            <div className="cv3-box cv3-box-sess">
              <div className="bl">Sessions fin/sch</div>
              <div className="cv3-sessgrid cv3-mono" style={{ fontSize: '13px' }}>
                <div>1on1: {one_on_one}</div>
                <div>GC: {group}</div>
              </div>
            </div>
            <div className="cv3-box cv3-box-act">
              <div className="bl">Replied</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                {replied_badge}
                {actions}
              </div>
            </div>
          </div>
          <div className="cv3-foot">
            <b>Onb</b> {student.onboarding_score} {onb_ico} &nbsp; <b>Act</b>{' '}
            {student.activity_score} &nbsp; <b>Cr</b> {student.credits} &nbsp;·&nbsp;{' '}
            {funnel || '--'}
          </div>
        </div>
      </div>
    );
  };

  close_ethnicity_popup = () => {
    this.setState({
      open_ethnicity_popup: false
    });
  };

  open_ethnicity_popup = (student) => {
    this.setState({
      open_ethnicity_popup: true,
      student_info: student,
      concierge_entered_ethnicity: student.concierge_entered_ethnicity
    });
  };

  get_excel_data = (students) => {
    const teacherDataArr = [];
    students.forEach((item) => {
      const csvData = {
        name: item?.name,
        uuid: item?.uuid,
        hridaya_profile_link: `https://hridaya.myyogateacher.com/studentprofile?id=${item?.uuid}`,
        day: this.get_student_day(item),
        first_1on1_session_status: this.get_first_1on1_status_label(
          item?.first_1on1_session_status
        ),
        email: item?.client_email,
        created_date: moment(item?.created_date)
          .tz(this.props.iana_timezone)
          .format('YYYY-MM-DD HH:mm:ss z'),
        funnel_url: item?.funnel_url,
        offer_type: item?.offer_type,
        funnel_type: item?.funnel_type,
        device_type: item?.device_type,
        yoga_level: item?.yoga_level,
        trial_end_date: moment(item?.trial_end_date)
          .tz(this.props.iana_timezone)
          .format('YYYY-MM-DD HH:mm:ss z'),
        affiliate: item?.affiliate,
        queue_affiliate: item?.queue_affiliate,
        credits: item?.credits,
        concierge_name: item?.concierge_name,
        is_member: item?.is_member,
        phone_personal: item?.phone_personal,
        yoga_sessions_finshed: item?.yoga_sessions_finshed,
        group_sessions_finshed: item?.group_sessions_finshed,
        yoga_sessions_scheduled: item?.yoga_sessions_scheduled,
        group_sessions_scheduled: item?.group_sessions_scheduled,
        activity_score: item?.activity_score,
        onboarding_score: item?.onboarding_score,
        iana_timezone: item?.iana_timezone,
        onboarding_complete: item?.onboarding_complete,
        client_age: item?.client_age
      };
      teacherDataArr.push(csvData);
    });
    return teacherDataArr;
  };

  // Shared with the render() lead-count pill, so the pill always matches what the
  // table is actually showing.
  get_filtered_students = () => {
    const {
      students,
      concierge_teacher_uuid,
      selected_iana_timezone,
      iana_abbr_list,
      first_1on1_filter,
      selected_day,
      member_filter
    } = this.state;
    let filter_students = [];
    if (concierge_teacher_uuid) {
      filter_students = students.filter((s) =>
        concierge_teacher_uuid === 'all'
          ? !!s.concierge_uuid
          : s.concierge_uuid === concierge_teacher_uuid
      );
    }
    if (!!selected_iana_timezone && selected_iana_timezone !== 'ALL') {
      const timezone = iana_abbr_list.find((i) => i.label === selected_iana_timezone);
      if (timezone) {
        filter_students = filter_students.filter((s) => s.iana_timezone_abbr === timezone.value);
      }
    }
    filter_students = this.apply_pod_filter(filter_students);
    // 1st 1on1 finished + Day filters (PROD-6787).
    if (first_1on1_filter !== 'ALL') {
      const want_finished = first_1on1_filter === 'YES';
      filter_students = filter_students.filter(
        (s) => (s.first_1on1_session_status === 'FINISHED') === want_finished
      );
    }
    if (selected_day !== 'ALL') {
      filter_students = filter_students.filter(
        (s) => this.get_student_day(s) === Number(selected_day)
      );
    }
    // Member filter: is_member already folds in active family-plan participation
    // (see get_concierge_students in apis).
    if (member_filter === 'MEMBERS') {
      filter_students = filter_students.filter((s) => s.is_member === 1);
    } else if (member_filter === 'NON_MEMBERS') {
      filter_students = filter_students.filter((s) => s.is_member !== 1);
    }
    return filter_students;
  };

  render_data = () => {
    const { sort_by } = this.state;
    // Apply the toolbar sort last, on the filtered set (no-op for DEFAULT).
    const filter_students = this.sort_students(this.get_filtered_students());
    const file_name = `concierge_users_lifestyle_{${moment().format('YYYY-MM-DD')}_${uuid()}`;
    return (
      <>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <span className="cv3-sortbar">
            <label htmlFor="cv3-sort" className="cv3-sortlbl">
              Sort by
            </label>
            <select
              id="cv3-sort"
              className="cv3-sortsel"
              value={sort_by}
              onChange={this.change_sort}
            >
              <option value="DEFAULT">Default</option>
              <option value="ONB_DESC">Onboarding score (High → Low)</option>
              <option value="ONB_ASC">Onboarding score (Low → High)</option>
              <option value="ACT_DESC">Activity score (High → Low)</option>
              <option value="ACT_ASC">Activity score (Low → High)</option>
            </select>
          </span>
          <ExcelFile
            element={<button className="data-download-btn m-0">Download Data</button>}
            filename={file_name}
          >
            <ExcelSheet data={this.get_excel_data(filter_students)} name="Latest Students">
              {conciergeV3DownloadList.map((d) => (
                <ExcelColumn key={d.value} label={d.label} value={d.value} />
              ))}
            </ExcelSheet>
          </ExcelFile>
        </div>

        {/* Lead-management list: dense aligned table on desktop/laptop, boxed card
            on tablet/mobile (container-query toggle at 820px). ONE record per row. */}
        <div className="cv3-list">
          <div className="cv3-legend">
            <span>
              <span className="dot dot-p" />
              Primary — Trial · Member · Sessions · Replied
            </span>
            <span>
              <span className="dot dot-s" />
              Secondary — Scores · Credits · Funnel · Concierge
            </span>
            <span className="r">sessions shown as fin / sch</span>
          </div>
          <div className="cv3-thead">
            <div>Name</div>
            <div>Trial</div>
            <div className="cv3-center">Member</div>
            <div className="cv3-center">Sessions</div>
            <div className="cv3-center">Replied</div>
            <div className="dimh">Details — onb · act · cr · funnel</div>
            <div className="rh">Actions</div>
          </div>
          {filter_students.map(this.render_mobile_student_card)}
        </div>
      </>
    );
  };

  close_history = () => this.setState({ history_open: false, open_info: false, student_info: {} });

  open_sms_window = (student) => {
    this.setState({
      sms_window_open: true,
      sms_student: student.uuid,
      sms_student_name: student.name,
      original_message_uuid: student.sms_uuid,
      message_dispatch_method: student.message_dispatch_method,
      is_allowed_whatsapp: student.is_allowed_whatsapp,
      is_allowed_sms: student.is_allowed_sms
    });
  };

  on_reply_sent = () => {
    const { students, sms_student } = this.state;
    const new_students_data = [...students];
    const student = new_students_data.find((s) => s.uuid === sms_student);
    if (student) {
      student.to_student = 1;
      this.setState({ students: new_students_data });
    }
  };

  close_sms_window = () => this.setState({ sms_window_open: false, sms_student: '' });

  render_info = () => {
    const { student_info } = this.state;
    return (
      <div>
        <p>Name - {student_info.name}</p>
        <p>Affiliate - {student_info.affiliate}</p>
        <p>Queue Table Affiliate - {student_info.queue_affiliate}</p>
        <p>
          Trial End -{' '}
          {moment(student_info.trial_end_date).tz(this.props.iana_timezone).format(day_format)}{' '}
          {moment(student_info.trial_end_date).tz(this.props.iana_timezone).format(time_format)}
        </p>
      </div>
    );
  };

  change_ethnicity_value = (e) => {
    e.preventDefault();
    this.setState({ concierge_entered_ethnicity: e.target.value });
  };

  submit_entered_ethnicity = async () => {
    this.setState({ loading: true });
    const url = get_api_url('/v2/cms_user/update_student_ethnicity', false, true);
    const payload = {
      student_uuid: this.state.student_info?.uuid,
      ethnicity: this.state.concierge_entered_ethnicity
    };
    try {
      await put_api(url, payload, true);
      this.setState({ loading: false });
      this.load_data();
      this.close_ethnicity_popup();
    } catch (e) {
      let err_message;
      try {
        err_message = e.response.data.message;
      } catch (err) {
        err_message = 'Some error occured. Please contact dev team.';
      }
      this.setState({ loading: false });
    }
  };

  render_change_ethnicity = () => (
    <>
      <CustomSingleSelect
        label="Ethnicity"
        options={EthnicityOptions}
        onChange={(e) => this.change_ethnicity_value(e)}
        value={this.state.concierge_entered_ethnicity}
        name="concierge_entered_ethnicity"
      />
      <button className="ct-small-btn" onClick={() => this.submit_entered_ethnicity()}>
        Submit
      </button>
    </>
  );

  render() {
    const {
      loading,
      history_open,
      history,
      open_info,
      sms_window_open,
      sms_student,
      sms_student_name,
      show_concierge_detail,
      original_message_uuid,
      open_ethnicity_popup,
      student_info,
      message_dispatch_method,
      is_allowed_sms,
      is_allowed_whatsapp
    } = this.state;
    return (
      <CommonHeader loading={loading} title="Concierge Users: MyYogaTeacher">
        <CustomModal show={history_open} close={this.close_history}>
          <div>{history}</div>
        </CustomModal>
        <CustomModal show={open_info} close={this.close_history}>
          {this.render_info()}
        </CustomModal>
        <div className="cv3-scope" style={{ minHeight: '400px' }}>
          {this.render_filters()}
          {this.render_data()}
        </div>
        {sms_window_open ? (
          <SmsWindow
            student_uuid={sms_student}
            student_name={sms_student_name}
            original_message_uuid={original_message_uuid}
            close_fn={this.close_sms_window}
            send_success_notification={this.on_reply_sent}
            message_dispatch_method={message_dispatch_method}
            is_allowed_whatsapp={is_allowed_whatsapp}
            is_allowed_sms={is_allowed_sms}
          />
        ) : null}
        <CustomModal show={show_concierge_detail} close={this.close_concierge_detail}>
          {this.concierge_profile()}
        </CustomModal>
        <CustomModal show={open_ethnicity_popup} close={this.close_ethnicity_popup}>
          {this.render_change_ethnicity(student_info)}
        </CustomModal>
        {!sms_window_open && (
          <div className="fixed bottom-4 right-4 z-40 rounded-full border border-line bg-surface-card px-8 py-4 text-xl font-medium text-content-primary shadow-lg">
            Leads available:{' '}
            <span className="font-bold">{this.get_filtered_students().length}</span>
          </div>
        )}
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone,
  teachers_list: state.teachers.teachers_list,
  teachers_list_status: state.teachers.teachers_list_status
});

const mapDispatchToProps = (dispatch) => ({
  get_teachers_list: (status) => {
    dispatch(actions.get_teachers_list(status));
  },
  set_notification_variable: (show, type, mes) => {
    dispatch(actions.set_notification_variable(show, type, mes));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergeV3);
